import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { tokenStorage } from './tokenManager';
import { AUTH_ENDPOINTS } from './endpoints/auth.endpoints';
import { TokenResponse } from '../types/authentication.types';
import { API_PREFIX } from './endpoints/api.constants';
import { expireIdleSession, isIdleExpired } from '@/auth/idleSession';
import { createBatchingAdapter } from './batchAdapter';
import { localizeApiMessage } from './apiMessages';
import { openInsightReport } from './openInsightReport';

const axiosInstance = axios.create({
  baseURL: "",
  timeout: 30000, // 30-second safety net — prevents permanent hangs
  headers: {
    'Content-Type': 'application/json',
  },
});

// GETs fired together are sent as one POST /v1/batch (see batchAdapter.ts).
axiosInstance.defaults.adapter = createBatchingAdapter(axios.getAdapter(axios.defaults.adapter));

// Many services/components call the client with a bare "/v1/…" (or "/api/v1/…")
// path. In dev Vite proxies that to the backend, but on prod the SPA and the
// API are different hosts, so those calls hit the SPA's nginx instead: GETs
// come back as index.html (200, no data) and writes fail with 405. Send them
// to the API. No-op in dev, where API_PREFIX is "/v1".
const RELATIVE_API_RE = /^\/(?:api\/)?v1(?=\/|\?|$)/;

axiosInstance.interceptors.request.use(
  (config) => {
    if (API_PREFIX && config.url && !config.baseURL && RELATIVE_API_RE.test(config.url)) {
      config.url = config.url.replace(RELATIVE_API_RE, API_PREFIX);
    }

    const token = tokenStorage.getAccessToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    let tenantId = localStorage.getItem('tenant_id');
    
    if (!tenantId && config.url?.includes('/auth/')) {
      tenantId = '1';
    }
    
    if (tenantId && config.headers) {
      config.headers['X-Tenant-ID'] = tenantId;
      config.headers['Tenant-Id'] = tenantId;
    }

    // The page's language: people's names (stored in several languages) come back in it.
    if (config.headers && !config.headers['X-Lang']) {
      config.headers['X-Lang'] = uiLanguage();
    }

    // A report shared with me is open (My reports): its data endpoints check the report's Access tab.
    const sharedReport = openInsightReport();
    if (sharedReport && config.headers) {
      config.headers['X-Insight-Report'] = sharedReport;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
// Pages a visitor uses signed out: a 401 there never sends them to the login page.
const onPublicPage = (p: string) => p.includes('/login') || p === '/' || p === '/catalog' || p.startsWith('/catalog/');
let failedQueue: Array<{ resolve: (value?: any) => void, reject: (reason?: any) => void }> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ── Global multilang stripping ────────────────────────────────────────────────
// The backend wraps translatable strings in {lang}…{lang} tags, e.g.
//   "{en}Introduction to Time Management{en}"
// This interceptor strips the wrappers from EVERY string in every response
// so that no component ever has to deal with them.

const MULTILANG_RE = /\{([a-zA-Z]{2,5})\}(.*?)\{\1\}/gs;

/** The active UI language (i18n/index.tsx stores it; switching language reloads the page). */
function uiLanguage(): string {
  try {
    return (localStorage.getItem('ilearn_active_locale') || 'en').toLowerCase();
  } catch {
    return 'en';
  }
}

// A run of adjacent blocks = one stored value ("{en}Hanna{en}{ar}هنا{ar}").
const MULTILANG_RUN_RE = /(?:\{([a-zA-Z]{2,5})\}[\s\S]*?\{\1\})(?:\{([a-zA-Z]{2,5})\}[\s\S]*?\{\2\})*/g;

/** Each stored value in the string resolved on its own, text around it kept: a full name built from two stored
 *  names ("{en}Hanna{en}{ar}هنا{ar} {en}Saleh{en}{ar}صالح{ar}") or a sentence with a course name in it comes out
 *  whole — this used to keep one block of the whole string (the last name only, or the course name only). */
function stripMultilang(raw: string): string {
  if (!raw.includes('{')) return raw;
  const lang = uiLanguage();
  let found = false;
  const out = raw.replace(MULTILANG_RUN_RE, (run) => {
    found = true;
    const map: Record<string, string> = {};
    for (const [, l, content] of run.matchAll(MULTILANG_RE)) map[l.toLowerCase()] = content.trim();
    // UAT DEF-185: the UI language first → English → first available.
    return map[lang] ?? map['en'] ?? Object.values(map)[0] ?? run;
  });
  return found ? out.trim() : raw;
}

// ── Upload links → API host ───────────────────────────────────────────────────
// The backend returns upload links RELATIVE ("/v1/uploads/file/84") on purpose:
// absolute URLs broke local Docker (internal hostnames). That works in dev,
// where Vite proxies /v1 to the backend, but on prod the SPA and the API live on
// different hosts, so a relative link resolves against the SPA and every
// <img>/<a> using it breaks (course page, cards, program covers, seminar
// photos, resource downloads, …). Point them at the API once, here. In dev
// API_PREFIX is "/v1", so this is a no-op.
// Also catches links some rows were saved with from a dev/docker backend
// ("http://localhost:8000/api/v1/uploads/file/68"), which no browser can load.
// Branding assets (tenant default covers, /tenants/<id>/branding/asset/<field>)
// are returned the same way and need the same treatment: a bare "/v1/…" only
// resolves when the API shares the SPA's origin, which on prod it doesn't.
const RELATIVE_UPLOAD_RE = /^(?:https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0|backend|useradmin[\w-]*)(?::\d+)?)?\/(?:api\/)?v1\/(uploads\/file\/|tenants\/\d+\/branding\/asset\/)/;

function absolutizeUpload(raw: string): string {
  return RELATIVE_UPLOAD_RE.test(raw) ? raw.replace(RELATIVE_UPLOAD_RE, `${API_PREFIX}/$1`) : raw;
}

/** A refusal's English `detail` → the interface language, when it is a known
 *  API message (see apiMessages.ts); the original stays in `detail_en`. */
function localizeErrorDetail(error: AxiosError): void {
  const data = error.response?.data as { detail?: unknown; detail_en?: string } | undefined;
  if (!data || typeof data !== 'object' || typeof data.detail !== 'string' || uiLanguage() === 'en') return;
  if (data.detail_en === undefined) {
    data.detail_en = data.detail;
    data.detail = localizeApiMessage(data.detail);
  }
}

/** One pass over the response: optional multilang stripping + upload links. */
function transformDeep(value: unknown, strip: boolean): unknown {
  if (typeof value === 'string') return absolutizeUpload(strip ? stripMultilang(value) : value);
  if (Array.isArray(value)) return value.map((v) => transformDeep(v, strip));
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = transformDeep(v, strip);
    }
    return out;
  }
  return value;
}

axiosInstance.interceptors.response.use(
  (response) => {
    // Skip blob / arraybuffer responses (file downloads)
    if (response.config?.responseType === 'blob' || response.config?.responseType === 'arraybuffer') {
      return response;
    }
    // Editors that ROUND-TRIP multilang strings (e.g. the Site Menu admin
    // page) need the raw {en}…{en}{ar}…{ar} values — stripping here would
    // silently destroy every non-English translation on save. They still get
    // working (absolute) upload links.
    const strip = !(response.config as { skipMultilangStrip?: boolean })?.skipMultilangStrip;
    if (response.data != null && typeof response.data === 'object') {
      response.data = transformDeep(response.data, strip);
    }
    return response;
  },
  async (error: AxiosError) => {
    localizeErrorDetail(error);
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Don't intercept 401s from auth endpoints (login, ldap-login) — those mean wrong credentials
    const isAuthEndpoint = originalRequest.url?.includes('/auth/') && 
      (originalRequest.url?.includes('/auth/') && !originalRequest.url?.includes('/auth/refresh'));
    
    if (
      error.response?.status !== 401 || 
      !originalRequest || 
      originalRequest._retry || 
      originalRequest.url?.includes(AUTH_ENDPOINTS.REFRESH_TOKEN) ||
      isAuthEndpoint
    ) {
      return Promise.reject(error);
    }

    // Session idle timeout: never silently renew a session the user has left
    // idle — background polling would otherwise keep it alive indefinitely.
    if (isIdleExpired()) {
      expireIdleSession();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
      .then(token => {
        if (originalRequest.headers && token) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
        }
        return axiosInstance(originalRequest);
      })
      .catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const currentRefreshToken = tokenStorage.getRefreshToken();
    
    if (!currentRefreshToken) {
      tokenStorage.clearTokens();
      isRefreshing = false;
      processQueue(new Error('No refresh token'), null);
      // Only redirect if not on login or a public page (guest dashboard, catalogue)
      if (!onPublicPage(window.location.pathname)) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    try {
      // AUTH_ENDPOINTS.REFRESH_TOKEN already carries API_PREFIX; adding it again as
      // baseURL posted to /v1/v1/auth/token/refresh (404) and every expired access
      // token became a forced re-login (UAT DEF-AU-010).
      const response = await axios.post<TokenResponse>(
        AUTH_ENDPOINTS.REFRESH_TOKEN,
        { refresh_token: currentRefreshToken },
        { 
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true 
        }
      );
      
      const { access_token, refresh_token } = response.data;
      
      tokenStorage.setTokens(access_token, refresh_token);

      if (axiosInstance.defaults.headers.common) {
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      }
      
      processQueue(null, access_token);
      
      if (originalRequest.headers) {
         originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
      }

      return axiosInstance(originalRequest);

    } catch (refreshError) {
      processQueue(refreshError as Error, null);
      tokenStorage.clearTokens();
      if (!onPublicPage(window.location.pathname)) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;